import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { meApi } from "../../api/me";
import { errorMessage } from "../../shared/errors";
import { formatDateTime } from "../../shared/format";
import { createPasskey, passkeysSupported } from "../../shared/webauthn";
import { Alert, Button, Card, Input } from "../../ui";

/** Passkeys (docs/phase6/P6-T02 §3): list, add through `navigator.credentials`, remove. */
export function PasskeysCard({
  onChanged,
}: {
  onChanged: () => Promise<unknown> | void;
}) {
  const queryClient = useQueryClient();
  const passkeys = useQuery({
    queryKey: ["me", "passkeys"],
    queryFn: meApi.passkeys,
  });
  const [name, setName] = useState("This device");
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["me", "passkeys"] });
    await onChanged();
  };
  const add = useMutation({
    mutationFn: async () => {
      const options = await meApi.passkeyOptions();
      const credential = await createPasskey(options);
      return meApi.addPasskey({ name, credential });
    },
    onSuccess: async (created) => {
      setMessage({ kind: "success", text: `Passkey "${created.name}" added.` });
      await refresh();
    },
    onError: (err) =>
      setMessage({
        kind: "error",
        text: errorMessage(err, "The passkey was not added."),
      }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => meApi.removePasskey(id),
    onSuccess: async () => {
      setMessage({ kind: "success", text: "Passkey removed." });
      await refresh();
    },
    onError: (err) => setMessage({ kind: "error", text: errorMessage(err) }),
  });
  return (
    <Card>
      <h2 className="mb-2 text-xl font-semibold">Passkeys</h2>
      <p className="mb-3 text-sm text-slate-600">
        Sign in with your device's screen lock or security key. A passkey also
        counts as your second step when you sign in with a password.
      </p>
      {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
      <ul className="divide-y divide-slate-100 text-sm">
        {(passkeys.data ?? []).map((p) => (
          <li key={p.id} className="flex items-center justify-between py-2">
            <div>
              <div className="font-medium">{p.name}</div>
              <div className="text-xs text-slate-500">
                added {formatDateTime(p.created_at)}
                {p.last_used_at
                  ? ` · last used ${formatDateTime(p.last_used_at)}`
                  : ""}
                {p.backed_up ? " · synced" : ""}
              </div>
            </div>
            <Button variant="secondary" onClick={() => remove.mutate(p.id)}>
              Remove
            </Button>
          </li>
        ))}
      </ul>
      {passkeysSupported() ? (
        <form
          className="mt-3 flex max-w-md items-end gap-2"
          aria-label="Add passkey"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Button type="submit" loading={add.isPending}>
            Add a passkey
          </Button>
        </form>
      ) : (
        <p className="mt-3 text-sm text-slate-500">
          This browser does not support passkeys.
        </p>
      )}
    </Card>
  );
}
